import React, { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { motion } from "framer-motion";
import { Plus, Trash2, Laptop, Loader2 } from "lucide-react";

export default function AdminShop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    image: "",
    description: "",
    specs: "",
  });

  /* ================= LOAD ================= */
  const loadProducts = async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, "products"));
    setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => {
    loadProducts();
  }, []);

  /* ================= SUBMIT ================= */
  const submit = async () => {
    if (!form.name || !form.image || !form.description) {
      alert("Please fill required fields");
      return;
    }

    setSaving(true);
    await addDoc(collection(db, "products"), {
      name: form.name,
      image: form.image,
      description: form.description,
      specs: form.specs
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
      createdAt: serverTimestamp(),
    });

    setForm({ name: "", image: "", description: "", specs: "" });
    setSaving(false);
    loadProducts();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await deleteDoc(doc(db, "products", id));
    loadProducts();
  };

  return (
    <div className="min-h-screen bg-[#0b0b0b] text-white px-6 py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-6xl mx-auto"
      >
        <h2 className="text-3xl font-bold mb-8 flex items-center gap-2">
          <Laptop /> Laptop Shop (Admin)
        </h2>

        {/* ADD PRODUCT */}
        <div className="bg-[#111214] border border-white/10 rounded-3xl p-6 mb-10">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Plus /> Add Laptop
          </h3>

          <div className="grid md:grid-cols-2 gap-4">
            <input
              placeholder="Laptop Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="px-4 py-3 rounded-xl bg-[#151820] border border-white/20"
            />

            <input
              placeholder="Image URL"
              value={form.image}
              onChange={(e) => setForm({ ...form, image: e.target.value })}
              className="px-4 py-3 rounded-xl bg-[#151820] border border-white/20"
            />
          </div>

          <textarea
            placeholder="Short Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="mt-4 w-full px-4 py-3 rounded-xl bg-[#151820] border border-white/20"
          />

          <textarea
            placeholder="Specs (one per line)"
            value={form.specs}
            onChange={(e) => setForm({ ...form, specs: e.target.value })}
            className="mt-4 w-full px-4 py-3 rounded-xl bg-[#151820] border border-white/20"
          />

          <button
            onClick={submit}
            disabled={saving}
            className="mt-6 px-6 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 font-semibold flex items-center gap-2"
          >
            {saving && <Loader2 className="animate-spin" />}
            Save Laptop
          </button>
        </div>

        {/* PRODUCT LIST */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {products.map((p) => (
              <div
                key={p.id}
                className="bg-[#111214] border border-white/10 rounded-2xl overflow-hidden"
              >
                <img
                  src={p.image}
                  alt={p.name}
                  className="h-40 w-full object-cover"
                />
                <div className="p-4">
                  <p className="font-semibold">{p.name}</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {p.description}
                  </p>

                  <button
                    onClick={() => remove(p.id)}
                    className="mt-3 text-red-400 flex items-center gap-1 text-sm"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
